/**
 * Department Head Employees Page
 * Read-only view of employees from the user's company and department
 * Reuses existing components and hooks for consistency
 */
import { useAuth } from "@/contexts/auth";
import { useEmployees } from "@/hooks/useQueryHooks";
import { User } from "@/types";
import { UserRole } from "@/types/enums";
import { Users, UserCheck, UserX } from "lucide-react";
import { EmployeesList } from "@/components/employee/sections/EmployeesList";
import { AssignRoleModal } from "@/components/employee/AssignRoleModal";
import { Button } from "@/components/ui/button";
import { useState } from "react";

export default function DepartmentHeadEmployeesPage() {
  const { user } = useAuth();
  const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false);
  
  // Get all departments for the user
  const userDepartments = Array.isArray(user?.department) 
    ? user.department 
    : user?.department ? [user.department] : [];
  
  // Use existing useEmployees hook for data fetching
  // The hook already supports multiple departments with department: string[]
  const { data: employeesData, isLoading, error } = useEmployees({
    department: userDepartments.length > 0 ? userDepartments : undefined,
    pageSize: 1000, // Get all employees for departments
    enabled: !!user?.companyId && userDepartments.length > 0
  });
  
  // Transform data to match User interface for compatibility
  const employees: User[] = (employeesData?.employees || []).map(emp => ({
    id: emp.id,
    name: emp.name || 'Unknown',
    email: emp.email || '',
    role: emp.role || UserRole.EMPLOYEE,
    department: emp.department || '',
    position: emp.position || '',
    shift: emp.shift || '',
    status: emp.status || 'Active',
    residency: emp.residency || 'Resident',
    workerType: emp.workerType || 'full_time',
    companyId: emp.companyId || null,
    employee_id: emp.employee_id || undefined
  }));
  
  // Calculate statistics
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter(emp => emp.status === 'Active').length;
  const inactiveEmployees = employees.filter(emp => emp.status !== 'Active').length;
  

  
  // Dummy functions for read-only view (no actions needed)
  const handleViewProfile = () => {};
  const handleEdit = () => {};
  const handleOvertimeHistory = () => {};
  const handleDepartmentChange = () => {};
  const handleStatusChange = () => {};

  // Show error if employee loading fails
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error Loading Employees</h3>
          <p className="text-red-600 mt-2">{error.message || 'Failed to load employees'}</p>
        </div>
      </div>
    );
  }

    return (
    <div className="container mx-auto p-6">
      {/* Employee Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50 p-4 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Employees</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{totalEmployees}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/50 p-4 rounded-lg border border-green-200/50 dark:border-green-800/50">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
              <UserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300">Active Employees</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">{activeEmployees}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/50 p-4 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <UserX className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Inactive Employees</p>
              <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{inactiveEmployees}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-muted-foreground">
          <span className="font-medium">Your Departments:</span> {userDepartments.length > 0 ? userDepartments.join(', ') : 'None assigned'}
        </div>
        <Button 
          variant="secondary" 
          onClick={() => setIsAssignRoleModalOpen(true)}
          className="h-10 px-4"
        >
          Assign Role
        </Button>
      </div>

      {/* Employee Data Table - Reuse existing EmployeesList component */}
      <EmployeesList
        users={employees}
        isLoading={isLoading}
        canCreateUsers={false} // Read-only view
        handleDepartmentChange={handleDepartmentChange}
        onViewProfile={handleViewProfile}
        onEdit={handleEdit}
        onViewOvertimeHistory={handleOvertimeHistory}
        onChangeStatus={handleStatusChange}
      />

      {/* Assign Role Modal */}
      <AssignRoleModal
        open={isAssignRoleModalOpen}
        onOpenChange={setIsAssignRoleModalOpen}
        userRole={user?.role}
        userDepartment={userDepartments}
        companyId={user?.companyId}
      />
    </div>
  );
}
